import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatTimestamp } from '@buzzfeed/bf-utils/lib/timestamp';
import { withTranslation } from '../../../../i18n';
import styles from './headline-timestamp.module.scss';

function isoDateString(ts) {
  const event = new Date(ts * 1000);
  return event.toISOString();
}

function formatAbsoluteTimestamp(timestampUnix, countryCode, options = {}, timeZone) {
  return new Intl.DateTimeFormat(countryCode, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
    ...(timeZone ? { timeZone } : {}),
  })
    .format(new Date(timestampUnix * 1000))
    .replace(' AM', ' am')
    .replace(' PM', ' pm');
}

function getTimestamp(timestampUnix, countryCode, options = {}, timeZone) {
  const relativeTimestamp = formatTimestamp(timestampUnix, countryCode, options);

  return {
    absolute: formatAbsoluteTimestamp(timestampUnix, countryCode, options, timeZone),
    relative: relativeTimestamp.hasTimestampString
      ? relativeTimestamp.formattedTimestamp
      : null,
  };
}

function Timestamp({
  className,
  countryCode,
  dateTime,
  formatOptions,
  isFuture,
  label,
  timestampUnix,
}) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Keep future timestamps deterministic: crawler clocks may lag behind the
  // publication time even though the upstream already serves the page as live.
  const useUtc = isFuture || !isClient;
  const { absolute, relative } = getTimestamp(
    timestampUnix,
    countryCode,
    formatOptions,
    useUtc ? 'UTC' : undefined
  );

  return (
    <time className={className} dateTime={dateTime}>
      {label} {absolute}{useUtc ? ' UTC' : ''}
      {/* A lagging clock would produce misleading future-relative text. */}
      {isClient && !isFuture && relative && (
        <span aria-hidden="true">
          <span className={styles.relativeSeparator}> · </span>
          {relative}
        </span>
      )}
    </time>
  );
}

Timestamp.defaultProps = {
  className: '',
  formatOptions: {},
  isFuture: false,
};

Timestamp.propTypes = {
  className: PropTypes.string,
  countryCode: PropTypes.string.isRequired,
  dateTime: PropTypes.string.isRequired,
  formatOptions: PropTypes.object,
  isFuture: PropTypes.bool,
  label: PropTypes.string.isRequired,
  timestampUnix: PropTypes.number.isRequired,
};

function HeadlineTimestamp({
  className,
  timestampPublishedUnix,
  timestampUpdatedUnix,
  showUpdatedTimestampOnly,
  countryCode,
  t,
  destination,
}) {
  const nowUnix = Date.now() / 1000;
  const hasPublishedTimestamp = timestampPublishedUnix > 0;

  if (!hasPublishedTimestamp || !countryCode) {
    return null;
  }

  // Some crawlers evaluate the page with a frozen or lagging clock, making an
  // otherwise live post appear future-dated to this render.
  const isFuturePublished = timestampPublishedUnix > nowUnix;
  const isNewsPage = destination === 'buzzfeed_news';
  const postCreatedIsoString = isoDateString(timestampPublishedUnix);
  const isUpdated = timestampUpdatedUnix > 0 && timestampUpdatedUnix <= nowUnix;
  const effectiveUpdatedTimestamp = isUpdated
    ? timestampUpdatedUnix
    : showUpdatedTimestampOnly
      ? timestampPublishedUnix
      : 0;
  const postUpdatedIsoString = effectiveUpdatedTimestamp
    ? isoDateString(effectiveUpdatedTimestamp)
    : null;
  let timestampCreated;
  let timestampUpdated;
  const newsFormatOptions = {
    month: 'long',
    hour: 'numeric',
    minute: 'numeric',
  };

  if (isFuturePublished) {
    timestampCreated = {
      formatOptions: isNewsPage
        ? newsFormatOptions
        : { hour: 'numeric', minute: 'numeric' },
      isFuture: true,
      timestampUnix: timestampPublishedUnix,
    };
  } else if (isNewsPage) {
    if (!showUpdatedTimestampOnly) {
      timestampCreated = {
        formatOptions: newsFormatOptions,
        timestampUnix: timestampPublishedUnix,
      };
    }
    if (effectiveUpdatedTimestamp) {
      timestampUpdated = {
        formatOptions: newsFormatOptions,
        timestampUnix: effectiveUpdatedTimestamp,
      };
    }
  } else if (isUpdated && timestampUpdatedUnix > timestampPublishedUnix) {
    timestampUpdated = { timestampUnix: timestampUpdatedUnix };
  } else {
    timestampCreated = { timestampUnix: timestampPublishedUnix };
  }

  return (
    <span className={`${isNewsPage ? styles.timestampNews : styles.timestamp} ${className ? className : ''} embed-timestamp`}>
      {!isNewsPage && timestampUpdated && (
        <Timestamp
          {...timestampUpdated}
          countryCode={countryCode}
          dateTime={postUpdatedIsoString}
          label={t('updated_on')}
        />
      )}
      {!isNewsPage && timestampCreated && (
        <Timestamp
          {...timestampCreated}
          countryCode={countryCode}
          dateTime={postCreatedIsoString}
          label={t('posted_on')}
        />
      )}
      {isNewsPage && <span className={`${styles.timestampNews} embed-timestamp`}>
        {timestampUpdated && (
          <Timestamp
            {...timestampUpdated}
            className={styles.timeDisplay}
            countryCode={countryCode}
            dateTime={postUpdatedIsoString}
            label={t('updated_on')}
          />
        )}
        {timestampCreated && (
          <Timestamp
            {...timestampCreated}
            className={styles.timeDisplay}
            countryCode={countryCode}
            dateTime={postCreatedIsoString}
            label={t('posted_on')}
          />
        )}
      </span>}
    </span>
  );
}

HeadlineTimestamp.defaultProps = {
  className: '',
  timestampPublishedUnix: 0,
  timestampUpdatedUnix: 0,
  showUpdatedTimestampOnly: false,
  countryCode: 'en-us',
  destination: 'buzzfeed',
};

HeadlineTimestamp.propTypes = {
  className: PropTypes.string,
  timestampPublishedUnix: PropTypes.number.isRequired,
  timestampUpdatedUnix: PropTypes.number.isRequired,
  showUpdatedTimestampOnly: PropTypes.bool,
  countryCode: PropTypes.string.isRequired,
  destination: PropTypes.string,
};

export default withTranslation('common')(HeadlineTimestamp);
